//@flow
import type {AuthResultEvent} from './AuthResultEvent';
import AbstractAuthType from './AuthType';
import {OAuthLoginQuery, OAuthLoginMsg, OAuthLoginResp, ZoomServerSpec} from '../autogenerated/ZoomBridge';
import ErrorId from '../protocol/ErrorId';
import Authenticator2 from './Authenticator2';
import AuthenticationController from '../controllers/AuthenticationController';
import AuthMechanism, { AuthRealm, NULL_OAUTH_REALM } from './AuthMechanism';
import Storage from '../protocol/Storage';
import Authenticator from '../protocol/Authenticator';
import ConnectionUtil from '../util/session/ConnectionUtil';
import System from "../basic/System";
import ViewUtil from '../ViewUtil';
import AssignUsersToCollection from '../queries/lightbox/AssignUsersToCollection';

const debug = require('../debug/debug')('wc:auth:OAuthType');

type Status = 'run' | 'abort' | 'poll' | 'idle';
export default class OAuthType extends AbstractAuthType {
  currentStatus: Status;
  nextStatus: Status;
  authResultEvent: AuthResultEvent;
  pollTimerId: any; //Need to use any for timer id as it was thing of NodeJs.Timer.
  pollCount: number;
  timer: any; //to save interval refs
  intiTimeDur: number;
  isAbort: boolean;
  resetOnFail: boolean;

  constructor(authRealm: AuthRealm) {
    super(authRealm);
    this.currentStatus = 'idle';
    this.authResultEvent = {};
    this.pollCount = 0;
    this.intiTimeDur = 2000;
    this.isAbort = false;
  }

  async login(resetOnFail:boolean = true): Promise<AuthResultEvent> {
    debug('[login()] in OAuthType');
    this.nextStatus = 'run';
    this.run();
    this.isAbort = false;
    this.resetOnFail = resetOnFail;
    return this.authResultEvent;
  }

  run(): void {
    switch (this.nextStatus) {
      case 'run':
        this.start();
        break;
      case 'poll':
        this.poll();
        break;
      case 'abort':
        this.abort();
        break;
      case 'idle':
        break;
    }
    this.currentStatus = this.nextStatus;
    this.nextStatus = 'idle';
  }

  static getExtraQueryParams(): Object {
    return {
      thiscommandonly: true,
      extclient: true,
      browserurl: ConnectionUtil.getPreviewServerUrl(),
      zm_app_name: 'webclient',
      zm_web_session: Storage.get('zm_web_session')
    };
  }

  issueQuery(message: OAuthLoginMsg): void {
    const oAuthQuery: OAuthLoginQuery = new OAuthLoginQuery();
    if (System.isBrowserApp())
      oAuthQuery.getAdditionalData = OAuthType.getExtraQueryParams;
    oAuthQuery.url = ConnectionUtil.getOAuthUrl();
    oAuthQuery.message = message;
    oAuthQuery.run().then(this.onOAuthQueryResponse, this.onOAuthQueryError);
  }

  start(): void {
    if (debug.enabled) {
      debug('[start()] Sending oauth fresh query');
    }
    const message: OAuthLoginMsg = new OAuthLoginMsg();
    message.setIsNew(true);
    message.setCancel(false);
    this.issueQuery(message);
  }

  onOAuthQueryError = (error: ErrorId) => {
    if (debug.enabled) {
      debug('[onOAuthQueryError(errorId: %o)]', error);
    }
    let errorMsg = '';
    let errorDetail = null;
    if (error) {
      errorMsg = error.toString();
      errorDetail = error.backendError;
      /* TODO: Have a mechanism to propagate the entire ErrorId as is.
          Alternatively, add a errorKey field to the OAuthLoginResp so that the ErrorId.errorKey may also be propagated.
       */
    }
    this.createAuthResultEvent(new OAuthLoginResp().setIsRunning(false).setSuccess(false).setSuccessMessage(errorMsg).setProgressMessage(errorDetail));
    this.dispatchAuthResultEvent();
  };

  onOAuthQueryResponse = (response: OAuthLoginResp) => {
    if (debug.enabled) {
      debug('[onOAuthQueryResponse(response: %o)]', response);
    }
    let isRunning:boolean = response.isIsRunning();
    if (isRunning)
      response.setSuccess(true);
    this.createAuthResultEvent(response);

    if (isRunning) {
      this.nextStatus = 'poll';
      //clearInterval(this.pollTimerId);
      this.setTimer();
      this.dispatchAuthResultEvent();
    } else {
      this.reset();
      if (debug.enabled) {
        debug('[onOAuthQueryResponse()] Finished polling or call is done.');
      }
      this.onLoginSuccess();
    }
  };

  poll(): void {
    if (debug.enabled) {
      debug('[poll()] Sending oauth poll query');
    }
    const message: OAuthLoginMsg = new OAuthLoginMsg();
    message.setIsNew(false);
    message.setCancel(false);
    this.issueQuery(message);
    this.pollCount++;
  }

  abort(): void {
    if (debug.enabled) {
      debug('[abort()] Aborting current oauth request');
    }
    const message: OAuthLoginMsg = new OAuthLoginMsg();
    message.setIsNew(false);
    message.setCancel(true);
    this.issueQuery(message);
    this.reset();
  }

  createAuthResultEvent(response: OAuthLoginResp): void {
    this.authResultEvent = {
      running: response.isIsRunning(),
      progress: response.getProgress(),
      progressMessage: response.getProgressMessage(),
      success: response.isSuccess(),
      successMessage: response.getSuccessMessage(),
      url: response.getUrl(),
      zoomServerSpec: response.getZoomServerSpec()
    };
  }

  dispatchAuthResultEvent(): void {
    Authenticator2.AUTH_STORE.put(Authenticator2.OAUTH_RESPONSE_DATA_EVENT, this.authResultEvent);
  }

  setTimer = () => {
    clearTimeout(this.timer);
    //return when Abort is trigger
    if (this.isAbort) {
      return;
    }
    //Check for timer Exhacted
    if (this.pollCount > 15) {
      this.intiTimeDur = 5000;
    }
    this.timer = setTimeout(() => {
      this.run();
    }, this.intiTimeDur);
    debug('[setTimer()] timer called', this.intiTimeDur);
  };

  removeTimer = () => {
    clearTimeout(this.timer);
  };

  reset(): void {
    //Set the local variables. So that this class can be re-used for login again.
    this.removeTimer();
    this.isAbort = true;
    this.currentStatus = 'idle';
    this.nextStatus = 'idle';
  }

  onLoginSuccess = () => {
    const serverSpec: ?ZoomServerSpec = this.authResultEvent.zoomServerSpec;
    this.reset();
    let dispatchAuthResultOnEnd: boolean = true;
    if (serverSpec) {
      Authenticator.AUTHENTICATOR_INSTANCE.authRealm = NULL_OAUTH_REALM;
      if (System.isBrowserApp()) {
        dispatchAuthResultOnEnd = false;
        AuthenticationController.signin(
          serverSpec.getZmUserName(),
          serverSpec.getZmCookie(),
          this.resetOnFail,
          true,
          serverSpec.getSessionId()
        )
        .then(() => (ViewUtil.isDirectLinkToCollection()
            ? new AssignUsersToCollection(ViewUtil.getCollectionIdFromArgs(), serverSpec.getZmUserName()).run()
            : new Promise((onSuccess, onFailure) => onSuccess(true))
        ))
        .finally(this.dispatchAuthResultEvent.bind(this));
      } else {
        AuthenticationController.onSignInSuccess(serverSpec);
      }
    }
    if (dispatchAuthResultOnEnd) {
      this.dispatchAuthResultEvent();
    }
  };
}
